import React, { useEffect, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import { Avatar, Box, Checkbox, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import Cancel from '@mui/icons-material/Cancel';
import { useDispatch, useSelector } from 'react-redux';
import { searchUser } from '../../redux/user/actions';
import _ from 'lodash';
import { IUserSummary } from '../../types/global/helper';
import { userSearchSelector, searchingUserSearchSelector } from '../AvatarGroup/selector';
import { showToastMessage } from '../../utils/AppUtils';

function stringToColor(str: string) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    let color = '#';
    for (let i = 0; i < 3; i++) color += `00${((hash >> (i * 8)) & 0xff).toString(16)}`.slice(-2);
    return color;
}

function getInitials(firstName?: string, lastName?: string, userName?: string) {
    if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
    if (firstName) return firstName[0].toUpperCase();
    if (userName) return userName.slice(0, 2).toUpperCase();
    return '?';
}

export interface IAvatarSelection {
    isEditMode?: boolean;
    title?: string;
    disableTitle?: boolean;
    placeholder?: string;
    value: IUserSummary[];
    onChange?: (selectedUsers: IUserSummary[]) => void;
    isSearchModalOpen?: boolean;
    singleSelection?: boolean;
    withProfileSearch?: boolean;
    strict?: boolean;
    /** Hide the absolute-positioned corner close (e.g. when the parent dialog already has a close control). */
    hideCornerClose?: boolean;
    /** Overrides checkbox selected / hover color (e.g. theme true highlight). */
    selectionAccentColor?: string;
}

const PeopleSearch: React.FC<IAvatarSelection> = ({
    placeholder,
    value,
    onChange,
    singleSelection = false,
    withProfileSearch,
    strict,
    hideCornerClose,
    selectionAccentColor,
}) => {
    const [selectedAvatar, setSelectedAvatar] = useState<IUserSummary[]>(value);
    const [initialSelection, setInitialSelection] = useState<IUserSummary[]>(value);
    const [searchValue, setSearchValue] = useState('');
    const [, setSearchModalOpen] = useState(false);
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const userResults = useSelector(userSearchSelector);
    const searchingUser = useSelector(searchingUserSearchSelector);

    useEffect(() => {
        if (selectedAvatar) {
            setInitialSelection(selectedAvatar);
        }
    }, [selectedAvatar]);

    const handleModalClose = () => {
        setSearchModalOpen(false);
    };

    const checkboxAccent = selectionAccentColor ?? theme[themeStyle].highlight;

    const handleSearch = useRef(
        _.debounce((searchTerm: string) => {
            if (searchTerm.length > 3 && !searchingUser) {
                dispatch(searchUser({ searchTerm, withProfileSearch, strict }));
            }
        }, 1000),
    ).current;

    const handleSearchInputChange = (event: any) => {
        const searchTerm = event.target.value;
        setSearchValue(searchTerm);
        handleSearch(searchTerm);
    };

    const handleClearSearch = () => {
        setSearchValue('');
        handleSearch.cancel();
    };

    const handleCheckboxChange = (user: IUserSummary) => {
        const isChecked = initialSelection?.some(a => a._id === user._id);

        let updated: IUserSummary[];
        if (isChecked) {
            updated = initialSelection.filter(u => u._id !== user._id);
        } else {
            if (singleSelection) {
                updated = [user];
            } else if (initialSelection.length < 9) {
                updated = [...initialSelection, user];
            } else {
                showToastMessage(`Maximum of 10 test users reached`);
                return;
            }
        }
        setInitialSelection(updated);
        setSelectedAvatar(updated);
        onChange && onChange(updated);
    };

    return (
        <Stack>
            <TextField
                autoFocus
                variant="outlined"
                placeholder={placeholder ?? `Search by name or email`}
                value={searchValue}
                onChange={handleSearchInputChange}
                InputProps={{
                    endAdornment: searchValue ? (
                        <InputAdornment position="end">
                            <IconButton
                                size="small"
                                onClick={handleClearSearch}
                                sx={{ color: '#a0a0a0', '&:hover': { color: '#fff' } }}>
                                <Cancel sx={{ fontSize: 16 }} />
                            </IconButton>
                        </InputAdornment>
                    ) : undefined,
                }}
                sx={{
                    minWidth: '100%',
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#262626',
                        color: '#fff',
                        fontSize: 14,
                        '& fieldset': { borderColor: '#484847', borderWidth: 1 },
                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.32)' },
                        '&.Mui-focused fieldset': { borderColor: '#e8c27d', borderWidth: 1 },
                    },
                    '& .MuiOutlinedInput-input': {
                        px: 2,
                        py: 1.5,
                        color: '#fff',
                        '&::placeholder': { color: '#a0a0a0', opacity: 1 },
                    },
                }}
            />
            {!hideCornerClose && (
                <Box sx={{ position: 'absolute', right: 0, top: 0 }}>
                    <IconButton
                        color="primary"
                        onClick={handleModalClose}
                        style={{ borderRadius: '50%', borderWidth: 1, borderColor: theme[themeStyle].lightWhite }}>
                        <Cancel color="secondary" />
                    </IconButton>
                </Box>
            )}
            {searchValue.length > 3 && (
                <Box
                    sx={{
                        mt: 1,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.08)',
                        bgcolor: '#1a1919',
                    }}>
                    <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
                        {userResults && userResults.length > 0 ? (
                            userResults.map((user, index) => {
                                const initials = getInitials(user.firstName, user.lastName, user.userName);
                                const bgColor = stringToColor(
                                    `${user.firstName || ''}${user.lastName || ''}${user._id || ''}`,
                                );
                                const isChecked = initialSelection.some(a => a._id === user._id);
                                return (
                                    <Stack
                                        key={index}
                                        flexDirection="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        onClick={() => handleCheckboxChange(user as unknown as IUserSummary)}
                                        sx={{
                                            px: 1.5,
                                            py: 1,
                                            cursor: 'pointer',
                                            borderBottom:
                                                index < userResults.length - 1
                                                    ? '1px solid rgba(255,255,255,0.05)'
                                                    : 'none',
                                            bgcolor: isChecked ? 'rgba(255,201,101,0.08)' : 'transparent',
                                            transition: 'background 0.12s',
                                            '&:hover': {
                                                bgcolor: isChecked
                                                    ? 'rgba(255,201,101,0.12)'
                                                    : 'rgba(255,255,255,0.04)',
                                            },
                                        }}>
                                        <Stack flexDirection="row" alignItems="center" gap={1.25}>
                                            <Avatar
                                                src={user.profile_picture || undefined}
                                                sx={{
                                                    width: 38,
                                                    height: 38,
                                                    bgcolor: bgColor,
                                                    fontSize: 13,
                                                    fontWeight: 700,
                                                }}>
                                                {initials}
                                            </Avatar>
                                            <Box>
                                                <Typography sx={{ color: '#fff', fontSize: 13, lineHeight: 1.3 }}>
                                                    {[user.firstName, user.lastName].filter(Boolean).join(' ') ||
                                                        user.userName ||
                                                        user.email}
                                                </Typography>
                                                {user.userName && (
                                                    <Typography sx={{ color: '#a0a0a0', fontSize: 11 }}>
                                                        @{user.userName}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                        <Checkbox
                                            checked={isChecked}
                                            onChange={() => {}}
                                            size="small"
                                            sx={{
                                                color: checkboxAccent,
                                                '&.Mui-checked': { color: checkboxAccent },
                                                p: 0.5,
                                            }}
                                        />
                                    </Stack>
                                );
                            })
                        ) : (
                            <Typography sx={{ color: '#a0a0a0', textAlign: 'center', py: 2.5, fontSize: 13 }}>
                                No users found
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}
        </Stack>
    );
};

export { PeopleSearch };
